import type { Bloque } from '@athena/shared';

/**
 * Lo que devuelve `POST /api/consulta/preguntas`, visto desde la web.
 *
 * Se declara aquí y no en `@athena/shared` a propósito: `shared` tiene la spec de bloques, que es
 * el contrato que los dos lados **comprueban**. Esto es la forma del JSON de una ruta, y meterlo
 * allí haría que un cambio de ruta obligase a reconstruir el paquete de los dos.
 *
 * Y vive en `common/services` y no en la feature porque el cliente del API lo necesita, y
 * `common/` no puede depender de una feature (`plan/PLAN.md` § 4.1).
 */
export interface FuenteDeLaRespuesta {
  readonly hechoId: string;
  readonly textoEn: string;
  /** Nula en los hechos que se citan por su página: gráfico, celda de tabla o página escaneada. */
  readonly cita: string | null;
  /** De dónde sale el dato en la página. Solo en el corpus por lotes. */
  readonly origen?: 'texto' | 'tabla' | 'grafico' | null;
  readonly cifra: string | null;
  /** Lo que hace falta para dibujar el hecho en un gráfico. Solo en el corpus por lotes. */
  readonly valor?: number | null;
  readonly unidad?: string | null;
  readonly divisa?: string | null;
  readonly periodo?: string | null;
  readonly base?: string | null;
  readonly mide?: string | null;
  readonly aproximado?: boolean | null;
  /** Dónde se midió, en código ISO 3166 / UN M49. */
  readonly ambito: string | null;
  /** A quién se preguntó, de la ficha de la fuente. */
  readonly poblacion: string | null;
  readonly verificado: boolean;
  readonly fuenteSlug: string;
  readonly editor: string;
  /** Quién lo paga, si el estudio lo declara. Es uno de los seis hechos de transparencia. */
  readonly financiador: string | null;
  readonly anio: number | null;
  readonly pagina: number | null;
  readonly localizador: string | null;
  readonly urlDocumento: string | null;
  /**
   * La página web del estudio. Se enlaza cuando no hay PDF: dos de las siete fuentes con datos
   * (2026-09-22) son artículos web, y sus citas se quedaban sin ningún enlace. Opcional porque las
   * respuestas guardadas antes no lo llevan.
   */
  readonly url?: string | null;
  readonly cabecera: string;
}

export type EstadoRespuesta = 'en_curso' | 'lista' | 'con_reservas' | 'fallida' | 'rechazada_tope';

export interface RespuestaDeAthena {
  readonly preguntaId: string;
  /** MODO-01: el modo con el que se respondió, y si lo eligió Athena. Las guardadas antes no lo traen. */
  readonly modo?: 'preguntar' | 'contrastar' | 'comparar';
  readonly modoAutomatico?: boolean;
  readonly estado: EstadoRespuesta;
  readonly bloques: readonly Bloque[];
  readonly referencias: readonly { readonly n: number; readonly hechoId: string }[];
  readonly fuentes: readonly FuenteDeLaRespuesta[];
  readonly supuestos: string;
  readonly sugerencias: readonly string[];
  readonly verificacion: { readonly juez: 'alta' | 'media' | 'baja' | null; readonly vueltas: number; readonly juezDisponible: boolean };
}

/**
 * Las facetas del catálogo y sus recuentos, tal como los devuelve `GET /corpus/fuentes`.
 *
 * Viven aquí con el resto de formas del API y no en la feature de fuentes porque el cliente los
 * necesita, y `common/` no puede depender de una feature.
 */
export interface RecuentoFacetado {
  readonly valor: string;
  readonly cuantas: number;
}

export type Faceta = 'pais' | 'tipoPoblacion' | 'editor' | 'anio';

export const FACETAS: readonly Faceta[] = ['pais', 'tipoPoblacion', 'editor', 'anio'];
